import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import CandidateStatus from 'core/enums/candidateStatus';
import Candidate from 'core/models/candidate';
import { DELETE, GET, PATCH, POST } from 'utils/http';

// Couche d'accès « état serveur » des candidats via TanStack Query.
// Le cache remplace l'ancien flag Redux `alreadyFetched` (candidatesAlreadyFetchedSelector
// devient `!useCandidatesQuery().isLoading` côté consommateurs), et l'invalidation de cette
// clé remplace les actions Set/Add/Update/Delete + refetch du provider.
//
// Deux providers alimentaient la même liste Redux :
//   - WithCandidates        -> GET /api/candidates
//   - WithAgencyCandidates  -> GET /api/agency/:agencyId/candidates
// On conserve EXACTEMENT ces deux endpoints. `fetchCandidates` choisit l'URL selon qu'un
// agencyId est fourni, et la clé de cache inclut l'agencyId pour ne pas mélanger les deux.
//
// La sélection multiple (selectedCandidatesIds) est un état d'UI : elle vit dans le store
// zustand `useCandidatesSelectionStore` (src/stores/candidatesSelection.ts), pas ici.

export const candidatesQueryKey = ['candidates'] as const;

// Clé dérivée pour la variante « candidats d'une agence ».
export const candidatesByAgencyQueryKey = (agencyId: string | undefined) =>
    [...candidatesQueryKey, 'agency', agencyId] as const;

export const fetchCandidates = async (agencyId?: string): Promise<Candidate[]> => {
    const url = agencyId
        ? `${BACKEND_URL}/api/agency/${agencyId}/candidates`
        : `${BACKEND_URL}/api/candidates`;
    const response = (await GET(url)) as Candidate[];

    return response ?? [];
};

// Hook liste de base : équivalent de candidatesSelector.
// Passer un agencyId reproduit le comportement de WithAgencyCandidates.
export const useCandidatesQuery = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
    });

// --- Hooks dérivés (1:1 depuis les anciens sélecteurs, transformations identiques) ---

// candidatesSelector(state) -> useCandidates()
export const useCandidates = (agencyId?: string) => useCandidatesQuery(agencyId);

// candidatesLengthSelector(state) -> useCandidatesLength()
export const useCandidatesLength = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) => candidates.length,
    });

// candidatesNeverConnectedSelector(state) -> useCandidatesNeverConnected()
export const useCandidatesNeverConnected = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter((candidate) => CandidateStatus.NeverConnected === candidate.status),
    });

// candidatesInTrialPeriodSelector(state) -> useCandidatesInTrialPeriod()
export const useCandidatesInTrialPeriod = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter((candidate) => CandidateStatus.InTrialPeriod === candidate.status),
    });

// candidatesWithoutLicenseAfterTrialPeriodSelector(state) -> useCandidatesWithoutLicenseAfterTrialPeriod()
export const useCandidatesWithoutLicenseAfterTrialPeriod = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter(
                (candidate) => CandidateStatus.WithoutLicenseAfterTrialPeriod === candidate.status,
            ),
    });

// candidatesWithPendingLicenseSelector(state) -> useCandidatesWithPendingLicense()
export const useCandidatesWithPendingLicense = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter((candidate) => CandidateStatus.WithPendingLicense === candidate.status),
    });

// candidatesWithValidLicenseSelector(state) -> useCandidatesWithValidLicense()
export const useCandidatesWithValidLicense = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter((candidate) => CandidateStatus.WithValidLicense === candidate.status),
    });

// candidatesWithExpiredLicenseSelector(state) -> useCandidatesWithExpiredLicense()
export const useCandidatesWithExpiredLicense = (agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) =>
            candidates.filter((candidate) => CandidateStatus.WithExpiredLicense === candidate.status),
    });

// candidateByIdSelector(state, id) -> useCandidateById(id)
export const useCandidateById = (id: string | undefined, agencyId?: string) =>
    useQuery({
        queryKey: agencyId ? candidatesByAgencyQueryKey(agencyId) : candidatesQueryKey,
        queryFn: () => fetchCandidates(agencyId),
        select: (candidates: Candidate[]) => candidates.find((candidate) => candidate.id === id),
        enabled: Boolean(id),
    });

// candidatesAlreadyFetchedSelector est OBSOLÈTE : le cache remplace ce gate.
// Côté consommateurs : useSelector(candidatesAlreadyFetchedSelector)
//   -> !useCandidatesQuery().isLoading (ou simplement retirer la garde).

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
// Invalide à la fois la liste globale et toutes les variantes par agence.
export const useInvalidateCandidates = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey });
};

// --- Mutations (une par action d'écriture des anciens consommateurs) ---

// Payload du formulaire de création (cf. CandidateForm `Inputs`).
// `type` (et non `interface`) pour rester assignable à HttpBody (Record<string, unknown>).
export type CreateCandidateInput = {
    civility: string;
    origin: string;
    email: string;
    firstName: string;
    job: string;
    lastName: string;
    mobileNumber: string;
    phoneNumber: string;
    agencyId: string;
};

// POST /api/agency/:agencyId/create-candidate -> remplace AddCandidate(candidate).
export const useCreateCandidateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agencyId, inputs }: { agencyId: string; inputs: CreateCandidateInput }) =>
            (await POST<Candidate>(`${BACKEND_URL}/api/agency/${agencyId}/create-candidate`, inputs)) as Candidate,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// POST /api/agency/:agencyId/allocate-license-to-candidate -> remplace UpdateCandidate(updated).
export const useAllocateLicenseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agencyId, candidateId }: { agencyId: string; candidateId: string }) =>
            (await POST<Candidate>(`${BACKEND_URL}/api/agency/${agencyId}/allocate-license-to-candidate`, {
                candidateId,
            })) as Candidate,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// PATCH /api/candidates/:candidateId/define-as-b2c -> remplace UpdateCandidate(updated).
// Conserve la transformation exacte de l'ancien defineAsB2CRequest.
export const useDefineAsB2CMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (candidateId: string): Promise<Candidate> => {
            const result = (await PATCH<Candidate>(
                `${BACKEND_URL}/api/candidates/${candidateId}/define-as-b2c`,
            )) as any;

            return {
                ...result,
                canBeSentAgainEmailInvitation: result.actions.sendInvitationEmail,
            };
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// PATCH /api/candidates/:candidateId/define-as-b2b -> remplace UpdateCandidate(updated).
// Conserve la transformation exacte de l'ancien defineAsB2BRequest.
export const useDefineAsB2BMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (candidateId: string): Promise<Candidate> => {
            const result = (await PATCH<Candidate>(
                `${BACKEND_URL}/api/candidates/${candidateId}/define-as-b2b`,
            )) as any;

            return {
                ...result,
                canBeSentAgainEmailInvitation: result.actions.sendInvitationEmail,
            };
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// POST /api/agency/:agencyId/send-new-email-invitation.
// N'altérait pas la liste Redux à l'origine, mais on invalide par sécurité (les
// `actions`/statuts côté serveur peuvent changer après renvoi d'invitation).
export const useSendNewEmailInvitationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agencyId, candidateId }: { agencyId: string; candidateId: string }) => {
            await POST<void>(`${BACKEND_URL}/api/agency/${agencyId}/send-new-email-invitation`, { candidateId });
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// DELETE /api/users/:candidateId -> remplace DeleteSelectedCandidates([id]).
export const useDeleteCandidateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (candidateId: string) => DELETE(`${BACKEND_URL}/api/users/${candidateId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};

// Suppression en masse (bulkDeleteCandidatesRequest) -> remplace DeleteSelectedCandidates(ids).
// La réinitialisation de la sélection après succès reste à la charge du consommateur
// via useCandidatesSelectionStore().clearSelectedCandidates().
export const useBulkDeleteCandidatesMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (candidateIds: string[]) => {
            await Promise.all(candidateIds.map((id) => DELETE(`${BACKEND_URL}/api/users/${id}`)));
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: candidatesQueryKey }),
    });
};
